import { useEffect, useRef } from 'react'
import { batch } from 'react-redux'
import { useAppDispatch, store } from '@/app/store'
import { useWebSocket } from '@/hooks/useWebSocket'
import { authService } from '@/services/api/shared/auth.service'
import { setPermissions } from '@/store/slices/permissions.slice'
import { setRegistryByScope, setRouteCatalogByScope } from '@/store/slices/navigation.slice'
import { setContextRefreshing, setContextStable } from '@/store/slices/auth.slice'
import { navigationService } from '@/services/api/shared/navigation.service'
import type { NavigationRouteCatalogEntry } from '@/services/api/shared/navigation.service'
import { registryToMenuPolicies, Scope } from '@/utils/navigation/registryToMenuPolicies'
import type { MenuPolicy } from '@/config/navigation/menu.config.generated.types'
import { logger } from '@/lib/logger'

export function PolicySync() {
  const dispatch = useAppDispatch()
  const latestRequestIdRef = useRef(0)
  const retryTimerRef = useRef<number | null>(null)

  useWebSocket()

  useEffect(() => {
    const clearRetryTimer = () => {
      if (retryTimerRef.current != null) {
        window.clearTimeout(retryTimerRef.current)
        retryTimerRef.current = null
      }
    }

    const scheduleRetry = (kind: 'permissions' | 'registry' | 'both') => {
      clearRetryTimer()
      retryTimerRef.current = window.setTimeout(() => {
        void syncPolicy(kind, false)
      }, 2000)
    }

    const fetchPermissions = async () => {
      return authService.getUIPermissions(undefined, true)
    }

    const fetchRegistry = async () => {
      const state = store.getState()
      const contextType = state.auth.contextData?.contextType as Scope | undefined
      if (!contextType) return {}
      try {
        const [raw, catalog] = await Promise.all([
          navigationService.getRegistry(contextType),
          navigationService.getRouteCatalog(contextType),
        ])
        return {
          registry: {
            [contextType]: registryToMenuPolicies(raw, contextType),
          } as Partial<Record<Scope, MenuPolicy[]>>,
          routeCatalog: {
            [contextType]: catalog,
          },
        }
      } catch {
        return {}
      }
    }

    const applyPolicyUpdate = (payload: {
      permissions?: Awaited<ReturnType<typeof fetchPermissions>>
      registry?: Partial<Record<Scope, MenuPolicy[]>>
      routeCatalog?: Partial<Record<Scope, NavigationRouteCatalogEntry[]>>
    }) => {
      batch(() => {
        if (payload.permissions) {
          dispatch(
            setPermissions({
              permissions: payload.permissions.permissions,
              userType: payload.permissions.userType,
              contextType: payload.permissions.contextType,
            })
          )
        }
        if (payload.registry) {
          dispatch(setRegistryByScope(payload.registry))
        }
        if (payload.routeCatalog) {
          dispatch(setRouteCatalogByScope(payload.routeCatalog))
        }
      })
    }

    const syncPolicy = async (kind: 'permissions' | 'registry' | 'both', allowRetry: boolean = true) => {
      const requestId = ++latestRequestIdRef.current
      dispatch(setContextRefreshing())
      try {
        if (kind === 'permissions') {
          const permissions = await fetchPermissions()
          if (requestId !== latestRequestIdRef.current) return
          applyPolicyUpdate({ permissions })
          return
        }

        if (kind === 'registry') {
          const fetched = await fetchRegistry()
          if (requestId !== latestRequestIdRef.current) return
          if (!fetched.registry || Object.keys(fetched.registry).length === 0) {
            throw new Error('Registry refresh returned empty payload')
          }
          applyPolicyUpdate({
            registry: fetched.registry,
            routeCatalog: fetched.routeCatalog,
          })
          return
        }

        // all-or-nothing for combined path: if either side fails, nothing is applied.
        const [permissions, fetched] = await Promise.all([fetchPermissions(), fetchRegistry()])
        if (requestId !== latestRequestIdRef.current) return
        if (!fetched.registry || Object.keys(fetched.registry).length === 0) {
          throw new Error('Registry refresh returned empty payload')
        }
        applyPolicyUpdate({
          permissions,
          registry: fetched.registry,
          routeCatalog: fetched.routeCatalog,
        })
      } catch (error) {
        if (requestId === latestRequestIdRef.current) {
          logger.error('[PolicySync] Policy refresh failed, keeping previous state', error)
          if (allowRetry) {
            scheduleRetry(kind)
          }
        }
      } finally {
        if (requestId === latestRequestIdRef.current) {
          dispatch(setContextStable())
        }
      }
    }

    const onSidebarInvalidated = async () => {
      await syncPolicy('registry')
    }

    const onPermissionsUpdated = async () => {
      await syncPolicy('permissions')
    }

    window.addEventListener('contextSidebarInvalidated', onSidebarInvalidated)
    window.addEventListener('permissionsUpdated', onPermissionsUpdated)
    window.addEventListener('roleUpdated', onPermissionsUpdated)
    return () => {
      clearRetryTimer()
      window.removeEventListener('contextSidebarInvalidated', onSidebarInvalidated)
      window.removeEventListener('permissionsUpdated', onPermissionsUpdated)
      window.removeEventListener('roleUpdated', onPermissionsUpdated)
    }
  }, [dispatch])

  return null
}

export default PolicySync
